import * as _ from "lodash";
import * as React from "react";
import classnames from "classnames";

import * as Models from "../Models";
import { selectLink, linkToRelativeUrl } from "./links";

export const getHeaderTitles: Function = (
    headers: string[],
    rows: Models.AttributeRow[]
) => {
    let headerTitles: any[] = headers;

    if (headerTitles.length === 0) {
        const longestRow: number =
            rows.length > 0
                ? _.maxBy(rows, (r) => r.items.length).items.length
                : 1;
        const tmp: any[] = [];
        for (let i: number = 0; i < longestRow; i++) {
            const headerName: string = `Column ${i + 1}`;
            tmp.push({
                headerName,
                headerAccessor: headerName.replace(/ /g, "_").toLowerCase(),
            });
        }
        headerTitles = tmp;
    } else {
        headerTitles = headerTitles.map((h: any) => {
            const headerName: string = h;
            return {
                headerName: h,
                headerAccessor: h.replace(/ /g, "_").toLowerCase(),
            };
        });
    }

    return headerTitles;
};

export const getData: Function = (
    headers: string[],
    rows: Models.AttributeRow[]
) => {
    return _.map(rows, (r) => {
        const items: Models.AttributeCell[] = r.items;
        const row: any = {};
        _.forEach(
            getHeaderTitles(headers, rows),
            (h: { headerName: string; headerAccessor: string }, i: number) => {
                const itemIndex = items.findIndex(
                    (item) => item.additionalInfo === h.headerAccessor
                );
                if (itemIndex === -1) {
                    row[h.headerAccessor] = items[i];
                } else {
                    row[h.headerAccessor] = items[itemIndex];
                }
            }
        );
        return row;
    });
};

export const getItemGroupData: Function = (
    headers: string[],
    rows: Models.AttributeRow[]
): Models.DataGridContent => {
    const result: Models.DataGridContent = {
        columns: [],
        data: [],
    };

    const headerTitles: any[] = getHeaderTitles(headers, rows);

    const customFilterMethod = (rows, columnId, filterValue) => {
        return rows.filter((row) => {
            const col = row.values[columnId]; // Get the value of the column
            let colDescription = "";

            if (col?.description && Array.isArray(col.description)) {
                colDescription =
                    col.description.length > 0 ? col.description[0] : "";
            } else {
                colDescription =
                    col?.description || col?.url?.description || "";
            }

            // If there's no description or it's null, skip this row
            if (colDescription === null || colDescription === "") {
                return false;
            }

            // Return rows that match the filter value (case-insensitive)
            return colDescription
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        });
    };

    const sortByDescription = (rowA, rowB, columnId, desc) => {
        const a = rowA.values[columnId];
        const b = rowB.values[columnId];

        // Get description from the row data
        const descriptionA = a?.description
            ? a.description
            : a?.url?.description || "";
        const descriptionB = b?.description
            ? b.description
            : b?.url?.description || "";

        // If either is null, return 0
        if (descriptionA === null || descriptionB === null) {
            return 0;
        }

        // If both descriptions are numbers, sort numerically
        if (_.isNumber(descriptionA) && _.isNumber(descriptionB)) {
            return descriptionA - descriptionB;
        }

        // Otherwise, sort alphabetically using localeCompare
        return descriptionA.toString().localeCompare(descriptionB.toString());
    };

    result.columns = headerTitles.map(
        (h: { headerName: string; headerAccessor: string }) => {
            return {
                Header: h.headerName,
                accessor: h.headerAccessor,
                Filter: ({ column }) => (
                    <input
                        type="text"
                        value={column.filterValue || ""}
                        onChange={(e) => column.setFilter(e.target.value)}
                        placeholder="Filter..."
                        className="form-control"
                    />
                ),
                filter: customFilterMethod,
                sortType: sortByDescription,
                Cell: (col: any) => {
                    const attributeCell: Models.AttributeCell = col.value;

                    let descriptionIsBoolean: Boolean = false;
                    let descriptionBooleanValue: string = "";

                    if (
                        attributeCell &&
                        attributeCell.description &&
                        attributeCell.description.constructor !== Array
                    ) {
                        const cellDescription = attributeCell.description
                            .toString()
                            .toLowerCase();
                        descriptionIsBoolean =
                            cellDescription === "true" ||
                            cellDescription === "false";
                        descriptionBooleanValue = descriptionIsBoolean
                            ? cellDescription === "true"
                                ? "Yes"
                                : "No"
                            : "";
                    }

                    if (
                        attributeCell &&
                        attributeCell.description &&
                        attributeCell.description.constructor === Array &&
                        (attributeCell.description as Array<any>).length > 0
                    ) {
                        attributeCell.description = (
                            attributeCell.description as Array<any>
                        ).reduce((first, second) => first + ", " + second);
                    }

                    return !attributeCell ? (
                        ""
                    ) : (
                        <span
                            title={
                                !descriptionIsBoolean
                                    ? attributeCell.toolTip ||
                                      attributeCell.description
                                    : descriptionBooleanValue
                            }
                        >
                            {attributeCell.url && attributeCell.url.id && (
                                <a
                                    href={linkToRelativeUrl(attributeCell.url)}
                                    onClick={(e) => {
                                        selectLink(e, attributeCell.url);
                                    }}
                                >
                                    {attributeCell.url.description}
                                </a>
                            )}
                            {!descriptionIsBoolean
                                ? attributeCell.description
                                : descriptionBooleanValue}
                        </span>
                    );
                },
                getProps: (state, rowInfo, column) => {
                    const className: string = getItemGroupColumnClass(
                        rows,
                        rowInfo,
                        column
                    );
                    return { className };
                },
            };
        }
    );

    result.data = getData(headers, rows);

    return result;
};

export const getItemGroupColumnClass: (
    rows: Models.AttributeRow[],
    rowInfo: any,
    column: any
) => string = (rows, rowInfo, column) => {
    const rowObject: Models.AttributeRow = rows[rowInfo.index];
    const obsoleteClass: any =
        rowObject &&
        rowObject.statusType !== undefined &&
        rowObject.statusType === Models.AttributeGroupItemRowStatusType.Obsolete
            ? "ItemGroup__column--obsolete"
            : "";
    const subGroupClass: string =
        _.has(rowInfo.row, "value") && rowInfo.row.value === undefined
            ? "ItemGroup__column--subGroup"
            : "";

    const customerActiveClass: string =
        rowInfo.original &&
        rowInfo.original.attribute &&
        rowInfo.original.attribute.description.toLowerCase() === "state" &&
        rowInfo.original.value &&
        rowInfo.original.value.description.toLowerCase() !== "active" &&
        column.id === "value"
            ? "ItemGroup__column--obsolete ItemGroup__column--obsoleteValue"
            : "";

    const normalizeTextClass: string =
        rowInfo.original &&
        rowInfo.original.id &&
        rowInfo.original.id.additionalInfo &&
        rowInfo.original.id.additionalInfo.toLowerCase() === "case-sensitive"
            ? "ItemGroup__column--normalizedTextGroup"
            : "";

    const className: string = classnames(
        "ItemGroup__column",
        subGroupClass,
        customerActiveClass,
        obsoleteClass,
        normalizeTextClass
    );
    return className;
};

export const getEditableItemGroupColumnClass: (
    column: any,
    rowInfo: any
) => string = (column, rowInfo) => {
    const original: any = rowInfo.original;
    const stateClassName: string =
        column.id === "value" &&
        original.attribute &&
        original.attribute.description.toLowerCase() === "state" &&
        original.value &&
        original.value.description.toLowerCase() === "obsolete"
            ? "ItemGroup__column--obsolete ItemGroup__column--obsoleteValue"
            : "";
    const operatingStatusClassName: string =
        column.id === "value" &&
        original.attribute &&
        original.attribute.description.toLowerCase() === "operating status" &&
        original.value &&
        original.value.description.toLowerCase() === "scrapped"
            ? "ItemGroup__column--obsolete ItemGroup__column--obsoleteValue"
            : "";
    const subGroupClass: string =
        _.has(rowInfo.row, "value") && rowInfo.row.value === undefined
            ? "ItemGroup__column--subGroup"
            : "";

    const normalizeTextClass: string =
        rowInfo.original &&
        rowInfo.original.column_1 &&
        rowInfo.original.column_1.additionalInfo &&
        rowInfo.original.column_1.additionalInfo.toLowerCase() ===
            "case-sensitive"
            ? "ItemGroup__column--normalizedTextGroup"
            : "";

    const className: string = classnames(
        "ItemGroup__column",
        subGroupClass,
        stateClassName,
        operatingStatusClassName,
        normalizeTextClass
    );

    return className;
};
